import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  WebsiteComposite,
  WebsiteSearchResponse,
  WebsiteCreation,
  WebsiteDeletion,
  Website,
  WebsiteToggle,
  WebsiteOrchestratorResources,
  WebsiteOrchestratorResource,
} from '../../shared/models/website.models';
import { Observable, of } from 'rxjs';
import {
  CreateWebsiteAliasDTO,
  CreateWebsiteDTO,
  CreateWebsiteStagingDTO,
  CreateWebsiteSubdomainDTO,
} from '../../shared/models/website-create.models';

@Injectable({
  providedIn: 'root',
})
export class WebsitesService {
  private http = inject(HttpClient);

  getWebsiteById(id: number): Observable<WebsiteComposite> {
    return this.http.get<WebsiteComposite>(`/websites/${id}`);
  }

  getWebsitesByIds(ids: number[]): Observable<WebsiteSearchResponse> {
    if (!ids.length) return of({ Websites: [], count: 0 });

    return this.http.get<WebsiteSearchResponse>(`/websites?ids=${ids.join(',')}`);
  }

  searchWebsites({
    page,
    pageSize,
    domainSearch,
    userId,
    serverSearch,
    statusSearch,
  }: {
    page?: number;
    pageSize?: number;
    domainSearch?: string;
    userId?: string;
    serverSearch?: string;
    statusSearch?: string;
  }): Observable<WebsiteSearchResponse> {
    const query = new URLSearchParams();
    if (userId) query.set('userid', userId);

    if (domainSearch) query.set('domain', domainSearch);

    if (serverSearch) query.set('server', serverSearch);

    if (statusSearch) query.set('status', statusSearch);

    if (page) query.set('page', String(page));

    if (pageSize) query.set('pageSize', String(pageSize));

    return this.http.get<WebsiteSearchResponse>(`/websites?${query.toString()}`);
  }

  getByServerAndDomain(
    server: string,
    domain?: string,
    page?: number,
  ): Observable<WebsiteSearchResponse> {
    const query = new URLSearchParams();
    query.set('server', server);

    if (domain !== undefined) {
      query.set('domain', domain);
    }

    if (page !== undefined) {
      query.set('page', String(page));
    }

    return this.http.get<WebsiteSearchResponse>(`/websites?${query.toString()}`);
  }

  getWebsiteCreations(id: number): Observable<WebsiteCreation[]> {
    return this.http.get<WebsiteCreation[]>(`/websites/${id}/creations`);
  }

  getWebsiteDeletions(id: number): Observable<WebsiteDeletion[]> {
    return this.http.get<WebsiteDeletion[]>(`/websites/${id}/deletions`);
  }

  createWebsite(data: CreateWebsiteDTO): Observable<Website> {
    return this.http.post<Website>('/websites', data);
  }

  createSubwebsite(data: CreateWebsiteSubdomainDTO): Observable<Website> {
    return this.http.post<Website>(`/websites/${data.websiteId}/subwebsite`, data);
  }

  createWebsiteStaging(data: CreateWebsiteStagingDTO): Observable<Website> {
    return this.http.post<Website>(`/websites/${data.websiteId}/staging`, data);
  }

  createWebsiteAlias(data: CreateWebsiteAliasDTO): Observable<Website> {
    return this.http.post<Website>(`/websites/${data.websiteId}/alias`, data);
  }

  deleteWebsite(websiteId: number): Observable<void> {
    return this.http.delete<void>(`/websites/${websiteId}`);
  }

  toggleWebsite(websiteId: number, status: 'ACTIVE' | 'DISABLED'): Observable<void> {
    return this.http.patch<void>(`/websites/${websiteId}`, { status });
  }

  getWebsiteToggles(id: number): Observable<WebsiteToggle[]> {
    return this.http.get<WebsiteToggle[]>(`/websites/${id}/toggles`);
  }

  recreateWebsite(id: number): Observable<void> {
    return this.http.patch<void>(`/websites/${id}/retry`, {});
  }

  retryAction(websiteId: number, creationId: number, actionId: number): Observable<void> {
    return this.http.patch<void>(`/websites/${websiteId}/retry/${creationId}/${actionId}`, {});
  }

  getWebsiteOrchestratorResources(websiteId: number): Observable<WebsiteOrchestratorResources> {
    return this.http.get(`/websites/${websiteId}/orchestrator`);
  }

  getWebsiteOrchestratorResource(
    websiteId: number,
    resourceId: number,
    resourceType: number,
  ): Observable<WebsiteOrchestratorResource> {
    return this.http.get<WebsiteOrchestratorResource>(
      `/websites/${websiteId}/orchestrator/${resourceType}/${resourceId}`,
    );
  }
}
